import React, { useState } from 'react'
import { Form, Input, Button, Card, Typography, Tabs, message, Checkbox } from 'antd'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, useLocation, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { courses } from '../data/courses'
import PageLayout from './PageLayout'
import PageControls from './PageControls'
import { getFirebaseErrorKey } from '../utils/firebaseErrorHandler'

const { Title, Text } = Typography
const { TabPane } = Tabs

const AuthPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('login')
  const [loginLoading, setLoginLoading] = useState(false)
  const [registerLoading, setRegisterLoading] = useState(false)
  const [guestLoading, setGuestLoading] = useState(false)
  const { login, register, signInAsGuest } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const { t, i18n } = useTranslation('auth')

  // Get course name from the route the user was trying to access
  const getCourseTitle = () => {
    const pathname = location.state?.from?.pathname
    if (!pathname) return null

    // Remove leading slash and find matching course
    const routePath = pathname.substring(1)
    const course = Object.values(courses).find(c => c.routePath === routePath)
    if (!course) return null

    // Get the course title from titleTranslations
    const currentLang = i18n.resolvedLanguage || 'en-GB'
    return course.titleTranslations?.[currentLang] || course.titleTranslations?.['en-GB'] || course.id.charAt(0).toUpperCase() + course.id.slice(1) + ' Course'
  }

  const courseTitle = getCourseTitle()

  const handleLogin = async (values: { email: string; password: string }) => {
    setLoginLoading(true)
    try {
      await login(values.email, values.password)
      message.success(t('messages.loginSuccess'))
      const from = location.state?.from?.pathname || '/'
      navigate(from)
    } catch (error: unknown) {
      const errorKey = getFirebaseErrorKey(error)
      const errorMessage = t(`messages.${errorKey}`)
      message.error(errorMessage)
    }
    setLoginLoading(false)
  }

  const handleRegister = async (values: {
    email: string
    password: string
    confirmPassword: string
    displayName?: string
    agreeTerms: boolean
    agreeMaterials: boolean
    agreePromoMaterials?: boolean
  }) => {
    if (values.password !== values.confirmPassword) {
      message.error(t('form.validation.passwordsDoNotMatch'))
      return
    }
    if (!values.agreeTerms) {
      message.error(t('form.validation.agreeTermsRequired'))
      return
    }
    if (!values.agreeMaterials) {
      message.error(t('form.validation.agreeMaterialsRequired'))
      return
    }
    setRegisterLoading(true)
    try {
      await register(
        values.email,
        values.password,
        values.displayName,
        values.agreeTerms,
        values.agreeMaterials,
        values.agreePromoMaterials
      )
      message.success(t('messages.registerSuccess'))
      const from = location.state?.from?.pathname || '/'
      navigate(from)
    } catch (error: unknown) {
      const errorKey = getFirebaseErrorKey(error)
      const errorMessage = t(`messages.${errorKey}`)
      message.error(errorMessage)
    }
    setRegisterLoading(false)
  }

  const handleGuestSignIn = async () => {
    setGuestLoading(true)
    try {
      await signInAsGuest()
      message.success(t('messages.guestLoginSuccess'))
      const from = location.state?.from?.pathname || '/'
      navigate(from)
    } catch (error: unknown) {
      const errorKey = getFirebaseErrorKey(error)
      const errorMessage = t(`messages.${errorKey}`)
      message.error(errorMessage)
    }
    setGuestLoading(false)
  }

  return (
    <PageLayout style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <Card
        style={{
          background: 'rgba(241, 248, 248, 0.637)',
          backdropFilter: 'blur(10px)',
          borderRadius: '16px',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          border: 'none',
          maxWidth: '500px',
          width: '100%',
          position: 'relative'
        }}
        bodyStyle={{ padding: '40px 32px' }}
      >
        {/* Language selector positioned at top right of card */}
        <div style={{ position: 'absolute', top: 16, right: 16, zIndex: 10 }}>
          <PageControls showUserProfile={false} />
        </div>
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <Title level={3} style={{ margin: 0, color: '#333', fontWeight: '800' }}>
            {courseTitle
              ? t('loginOrRegisterWithCourse', { courseTitle })
              : t('loginOrRegister')
            }
          </Title>
          <Text type="secondary" style={{ display: 'block', marginTop: '4px' }}>
            {t('signInDescription')}
          </Text>
        </div>

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          centered
          size="large"
          tabBarStyle={{
            borderBottom: '2px solid #9c8b904b',
            marginBottom: '16px'
          }}
        >
          <TabPane tab={t('tabs.login')} key="login">
            <Form onFinish={handleLogin} layout="vertical" size="large">
              <Form.Item
                label={t('form.email')}
                name="email"
                rules={[{ required: true, type: 'email', message: String(t('form.validation.requiredEmail')) }]}
              >
                <Input placeholder={String(t('form.placeholders.email'))} />
              </Form.Item>
              <Form.Item
                label={t('form.password')}
                name="password"
                rules={[{ required: true, message: String(t('form.validation.requiredPassword')) }]}
              >
                <Input.Password placeholder={String(t('form.placeholders.password'))} />
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loginLoading}
                  block
                  size="large"
                  style={{
                    height: '40px',
                    borderRadius: '8px',
                    fontWeight: '600'
                  }}
                >
                  {t('form.buttons.signIn')}
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab={t('tabs.register')} key="register">
            <Form
              onFinish={handleRegister}
              layout="vertical"
              size="middle"
              style={{ gap: 8, display: 'flex', flexDirection: 'column' }}
            >
              <Form.Item
                label={t('form.email')}
                name="email"
                rules={[{ required: true, type: 'email', message: String(t('form.validation.requiredEmail')) }]}
                style={{ marginBottom: 12 }}
              >
                <Input placeholder={String(t('form.placeholders.email'))} />
              </Form.Item>
              <Form.Item
                label={t('form.displayName')}
                name="displayName"
                rules={[{ max: 50, message: String(t('form.validation.displayNameMaxLength')) }]}
                style={{ marginBottom: 12 }}
              >
                <Input placeholder={String(t('form.placeholders.displayName'))} />
              </Form.Item>
              <Form.Item
                label={t('form.password')}
                name="password"
                rules={[{ required: true, message: String(t('form.validation.requiredPassword')) }]}
                style={{ marginBottom: 12 }}
              >
                <Input.Password placeholder={String(t('form.placeholders.createPassword'))} />
              </Form.Item>
              <Form.Item
                label={t('form.confirmPassword')}
                name="confirmPassword"
                rules={[{ required: true, message: String(t('form.validation.requiredConfirmPassword')) }]}
                style={{ marginBottom: 12 }}
              >
                <Input.Password placeholder={String(t('form.placeholders.confirmPassword'))} />
              </Form.Item>
              <Form.Item
                name="agreeTerms"
                valuePropName="checked"
                rules={[{ required: true, message: String(t('form.validation.agreeTermsRequired')) }]}
                style={{ marginBottom: 8 }}
              >
                <Checkbox>
                  {t('form.checkboxes.agreeTerms')}{' '}
                  <Link to="/legal-notice" target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff', textDecoration: 'underline' }}>
                    {t('form.checkboxes.agreeTermsLegalNotice')}
                  </Link>
                  {' '}{t('form.checkboxes.agreeTermsAnd')}{' '}
                  <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: '#1890ff', textDecoration: 'underline' }}>
                    {t('form.checkboxes.agreeTermsPrivacyPolicy')}
                  </Link>
                </Checkbox>
              </Form.Item>
              <Form.Item
                name="agreeMaterials"
                valuePropName="checked"
                rules={[{ required: true, message: String(t('form.validation.agreeMaterialsRequired')) }]}
                style={{ marginBottom: 8 }}
              >
                <Checkbox>
                  {t('form.checkboxes.agreeMaterials')}
                </Checkbox>
              </Form.Item>
              <Form.Item
                name="agreePromoMaterials"
                valuePropName="checked"
                style={{ marginBottom: 16 }}
              >
                <Checkbox>
                  {t('form.checkboxes.agreePromoMaterials')}
                </Checkbox>
              </Form.Item>
              <Form.Item style={{ marginBottom: 0 }}>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={registerLoading}
                  block
                  size="large"
                  style={{
                    height: '40px',
                    borderRadius: '8px',
                    fontWeight: '600'
                  }}
                >
                  {t('form.buttons.createAccount')}
                </Button>
              </Form.Item>
            </Form>
          </TabPane>

          <TabPane tab={t('tabs.guest')} key="guest">
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <Text type="secondary">
                {t('form.guestDescription')}
              </Text>
            </div>
            <Button
              type="primary"
              block
              size="large"
              loading={guestLoading}
              onClick={handleGuestSignIn}
              style={{
                height: '40px',
                borderRadius: '8px',
                fontWeight: '600'
              }}
            >
              {t('form.buttons.continueAsGuest')}
            </Button>
          </TabPane>
        </Tabs>
      </Card>
    </PageLayout>
  )
}

export default AuthPage